import React, { useState, useMemo, useCallback } from 'react';
import './WinnersStrip.css';
import { useSelector } from 'react-redux';
import { useAsgActions } from '../../../../../shared/src/hooks/use-asg-actions.hook';
import { useCallbackOnInitAndResizeHook } from '../../../../../shared/src/hooks/use-callback-on-init-and-resize.hook';
import { useScreenWidthLessHook } from '../../../../../shared/src/hooks/use-screen-width-less.hook';
import { ThumbnailLoading } from '../../../../../shared/src/components/ThumbnailLoading';
import {
    FlyingSliderArrow,
    FlyingSliderArrowTypesEnum,
} from '../../../../../shared/src/components/FlyingSliderArrow';
import { WinnersStripItem } from '../WinnersStripItem';
import { Navigation, Autoplay } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import 'swiper/css/autoplay';
import 'swiper/css/navigation';
import {
    GtmEventLocation,
    GtmEventComponent,
} from '../../../../../shared/src/components/google-tag-manager/enums/gtm-event-enum';
import { useCurrentVerticalData } from '../../../../../shared/src/hooks/use-current-vertical-data.hook';
import { useSwiperTracking } from '../../../../../shared/src/hooks/use-swiper-tracking.hook';

export const WinnersStrip = ({
    parentWidth,
}) => {
    const { loadGame } = useAsgActions();
    const [isMobile, setIsMobile] = useState(false);
    const [visibleItemsNum, setVisibleItemsNum] = useState(0);
    const { winnersStripConfig } = useSelector((state) => state.app.config);
    const winners = useSelector((state) => state.winners.list);

    const {
        pamGameVerticalID,
        currentVerticalType,
    } = useCurrentVerticalData();
    const {
        onTouchStartHandler,
        onArrowClickHandler,
        onSlideChangeHandler,
    } = useSwiperTracking({
        location: GtmEventLocation.GAME_GRID,
        component: GtmEventComponent.WINNER_TICKER,
        source: 'winners',
        type: 'winners',
        verticalId: pamGameVerticalID,
        verticalType: currentVerticalType,
    });

    const winnerItemsGap = isMobile ? 12 : 16;
    const getDesktopItemWidth = useCallback(() => {
        const windowWidth = window.innerWidth;
        let winnerItemWidth = 163;
        if (windowWidth < 1440) {
            winnerItemWidth = 160;
        } else if (windowWidth >= 1920) {
            winnerItemWidth = 186;
        }
        return winnerItemWidth;
    }, []);
    const visibleItemsNumCalc = useCallback(() => {
        const winnerItemWidth = isMobile ? 120 : getDesktopItemWidth();
        const wrapperWidth = parentWidth || window.innerWidth;
        // To avoid issue with loop, Swiper needs more elements than visible
        setVisibleItemsNum(Math.ceil(wrapperWidth / (winnerItemWidth + winnerItemsGap)) + 1);
    }, [isMobile, parentWidth]);

    useScreenWidthLessHook({ setIsWindowWidthLess: setIsMobile });
    useCallbackOnInitAndResizeHook({ callback: visibleItemsNumCalc });

    const loaders = (new Array(visibleItemsNum)).fill(null);
    const winnersToShow = useMemo(() => {
        const winnersNum = winners?.length;

        if (!winnersNum) {
            return [];
        }

        const timestamp = Date.now();
        /**
         * Avoid too close elements' duplication.
         *
         * E.g., if to use higher value, then with 2 real, and 3 visible,
         * loop will be: 1 2 1 [1 2 1]. So two 1st one by one.
         *
         * With multiplier, it will be 1 2 1 2 [1 2 1 2]
         */
        const itemsMultiplier = Math.ceil(visibleItemsNum / winnersNum);
        const winnersNumToShow = winnersNum >= visibleItemsNum ? winnersNum : winnersNum * itemsMultiplier;

        const refilledWinners = [];
        for (let i = 0; i < winnersNumToShow; i++) {
            refilledWinners.push({
                key: `${timestamp}-${i}`,
                data: winners[i % winnersNum],
            });
        }

        return refilledWinners;
    }, [winners, visibleItemsNum]);

    let autoplayConfig = false;
    if (winnersStripConfig?.scrollInterval) {
        autoplayConfig = {
            delay: winnersStripConfig.scrollInterval,
            pauseOnMouseEnter: true,
            disableOnInteraction: false,
        };
    }

    const areWinnersReady = !!winnersToShow?.length;

    return <>
        {!areWinnersReady && <div className="winners-strip__loading">
            {loaders.map((_, i) => <ThumbnailLoading
                key={i}
                iconName="winner"
                className="winners-strip__loading-item"
            />)}
        </div>}

        {areWinnersReady && <div className="winners-strip__items">
            <FlyingSliderArrow
                extraClass="winners-strip-button"
                onClick={onArrowClickHandler}
            />
            <Swiper
                modules={[Navigation, Autoplay]}
                spaceBetween={winnerItemsGap}
                slidesPerView="auto"
                speed={1500}
                loop={true}
                autoplay={autoplayConfig}
                navigation={{
                    enabled: !isMobile,
                    prevEl: `.winners-strip-button--${FlyingSliderArrowTypesEnum.PREV}`,
                    nextEl: `.winners-strip-button--${FlyingSliderArrowTypesEnum.NEXT}`,
                }}
                onSlideChangeTransitionEnd={onSlideChangeHandler}
                onTouchStart={onTouchStartHandler}
            >
                {winnersToShow.map((winner) => <SwiperSlide key={winner.key}>
                    <WinnersStripItem
                        winner={winner.data}
                        playNowHandler={loadGame}
                    />
                </SwiperSlide>)}
            </Swiper>
            <FlyingSliderArrow
                extraClass="winners-strip-button"
                arrowType={FlyingSliderArrowTypesEnum.NEXT}
                onClick={onArrowClickHandler}
            />
        </div>}
    </>;
};
